import { keccak256, toUtf8Bytes } from 'ethers';
import { convertMethodSignatureToPrototype } from './abiSignature';
import { md5Bytes } from './md5';

/**
 * Name-based UUID v3 (MD5), matching Java UUID.nameUUIDFromBytes.
 */
export function nameUuidV3(key: string): string {
  const d = md5Bytes(key);
  d[6] = (d[6] & 0x0f) | 0x30;
  d[8] = (d[8] & 0x3f) | 0x80;
  const h = Array.from(d)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Canonical Solidity ABI signature (no spaces, uint→uint256, etc.). */
export function canonicalizeAbiSignature(methodSignature: string): string {
  const prototype = convertMethodSignatureToPrototype(methodSignature.trim());
  const match = prototype.match(/^(\w+)\((.*)\)$/);
  if (!match) return prototype.replace(/\s+/g, '');

  const name = match[1];
  const paramsString = match[2];
  if (!paramsString.trim()) return `${name}()`;

  const types = splitTopLevel(paramsString).map(normalizeAbiType);
  return `${name}(${types.join(',')})`;
}

function splitTopLevel(paramsString: string): string[] {
  const result: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of paramsString) {
    if (ch === '(' || ch === '[') depth += 1;
    else if (ch === ')' || ch === ']') depth = Math.max(0, depth - 1);
    else if (ch === ',' && depth === 0) {
      if (current.trim()) result.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.trim()) result.push(current.trim());
  return result;
}

function normalizeAbiType(raw: string): string {
  let t = raw
    .trim()
    .replace(/\bmemory\b/g, '')
    .replace(/\bcalldata\b/g, '')
    .replace(/\bstorage\b/g, '')
    .replace(/\bindexed\b/g, '')
    .replace(/\s+/g, '');

  // uint / int without bit-width → uint256 / int256 (preserve array suffixes)
  t = t.replace(/\buint(?!\d)/g, 'uint256').replace(/\bint(?!\d)/g, 'int256');
  return t;
}

export function evmFunctionSelector(canonicalSignature: string): string {
  const hash = keccak256(toUtf8Bytes(canonicalSignature));
  return `0x${hash.slice(2, 10).toLowerCase()}`;
}

export function evmActionId(
  chainId: string,
  contractAddress: string,
  methodSignatureOrPrototype: string
): string {
  const canonical = canonicalizeAbiSignature(methodSignatureOrPrototype);
  const selector = evmFunctionSelector(canonical);
  const key = `${chainId}|${contractAddress.toLowerCase()}|${selector}`;
  return nameUuidV3(key);
}

export function stellarActionId(
  chainId: string,
  contractAddress: string,
  methodSignatureOrPrototype: string
): string {
  const actionName = methodSignatureOrPrototype.split('(')[0].trim().toLowerCase();
  const key = `${chainId}|${contractAddress.toLowerCase()}|${actionName}`;
  return nameUuidV3(key);
}

export function solanaActionId(
  chainId: string,
  program: string,
  instructionSelector: string
): string {
  const key = `${chainId}|${program.toLowerCase()}|${instructionSelector.toLowerCase()}`;
  return nameUuidV3(key);
}

export type ActionEngine = 'evm' | 'stellar' | 'solana' | string;

/**
 * Compute action ID for a method display signature (EVM/Stellar) or selector (Solana).
 */
export function computeActionId(
  engine: ActionEngine,
  chainId: string,
  contractAddress: string,
  methodOrSelector: string
): string {
  if (engine === 'stellar') {
    return stellarActionId(chainId, contractAddress, methodOrSelector);
  }
  if (engine === 'solana') {
    return solanaActionId(chainId, contractAddress, methodOrSelector);
  }
  return evmActionId(chainId, contractAddress, methodOrSelector);
}

/**
 * Map actionId → method display signature by recomputing IDs from ABI/SEP-48 methods.
 */
export function buildActionIdMethodLookup(
  engine: ActionEngine,
  chainId: string,
  contractAddress: string,
  methods: string[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const method of methods) {
    try {
      const id = computeActionId(engine, chainId, contractAddress, method);
      if (!map.has(id)) {
        map.set(id, method);
      }
    } catch {
      // skip unparseable methods
    }
  }
  return map;
}
